import React, { FC } from "react";
import { JobPostEditorSectionLayout } from "@components/Layout/JobPostEditorSectionLayout";
import { CompensationUnit, CurrencyCode } from "@prisma/client";
import { useFormContext } from "react-hook-form";
import { Select } from "@components/Form/Select";
import { Input } from "@components/Form/Input";
import { compensationUnitToLabel } from "@utils/enumToLabel/compensationUnitToLabel";
import { TagsGroup } from "@components/TagsGroup/TagsGroup";
import { NewTag, TagType } from "types/tags";
import { currencies } from "data/currencies";
import { classNames } from "@utils/classNames";

interface Props {
  tags: NewTag[];
}

export const CompensationSection: FC<Props> = ({ tags }) => {
  const {
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext();
  const hasCompensation = watch("jobPost.hasCompensation");

  return (
    <JobPostEditorSectionLayout
      title="Compensation & Benefits"
      description="Provide salary details & any extras like stock options etc. Job posts with compensation rank higher in search results."
      hasToggle
      isToggled={hasCompensation}
      onToggle={() => setValue("jobPost.hasCompensation", !hasCompensation)}
    >
      <div className="flex flex-row space-x-2">
        <div className="col-span-4">
          <Select
            label="Currency"
            path="jobPost.compensation.currency"
            options={[
              CurrencyCode.USD,
              CurrencyCode.GBP,
              CurrencyCode.EUR,
              CurrencyCode.CNY,
              ...Object.keys(currencies).filter(
                (c) =>
                  ![
                    CurrencyCode.USD,
                    CurrencyCode.GBP,
                    CurrencyCode.EUR,
                    CurrencyCode.CNY,
                    //@ts-ignore
                  ].includes(c as CurrencyCode)
              ),
            ].map((type, i) => (
              <option
                key={type}
                value={type}
                className={classNames(i === 3 ? "border-t" : "")}
              >
                {type} - {currencies[type]}
              </option>
            ))}
            register={register}
            defaultValue={CurrencyCode.USD}
            errors={errors}
          />
        </div>
        <div className="col-span-4">
          <Select
            label="Unit"
            path="jobPost.compensation.unit"
            options={[
              CompensationUnit.HOUR,
              CompensationUnit.DAY,
              CompensationUnit.WEEK,
              CompensationUnit.MONTH,
              CompensationUnit.YEAR,
            ].map((type) => (
              <option key={type} value={type}>
                {compensationUnitToLabel[type]}
              </option>
            ))}
            register={register}
            errors={errors}
          />
        </div>
        <div className="col-span-6">
          <Input
            label="Min"
            hasOptionalBadge
            path="jobPost.compensation.min"
            placeholder="30000"
            register={register}
            registerOptions={{
              setValueAs: (v: string) =>
                parseInt(v) !== NaN ? parseFloat(v) : null,
            }}
            errors={errors}
          />
        </div>

        <div className="col-span-6">
          <Input
            label="Max"
            path="jobPost.compensation.max"
            placeholder="50000"
            register={register}
            registerOptions={{
              setValueAs: (v: string) =>
                parseInt(v) !== NaN ? parseFloat(v) : null,
            }}
            errors={errors}
          />
        </div>
      </div>
      <div className="col-span-6 sm:col-span-6 lg:col-span-2 mt-6">
        <TagsGroup
          heading="Benefits"
          availableTagTypes={[TagType.COMPENSATION_BENEFIT, TagType.JOB]}
          tags={tags}
          name="jobPost.userAssignedTags"
        />
      </div>
    </JobPostEditorSectionLayout>
  );
};
